import React, { useState, useMemo, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { useALCContext } from '../context/ALCContext';
import { alcService } from '../services/alcService';
import ALCStepper from './ALCStepper';
import { setALCConfig, addPendingSync } from '../../../../store/ALCSlice';
import { useMsal } from '@azure/msal-react';
import { SP_MSAL_SCOPE } from '../../../../constants/sharepoint';
import store from '../../../../store/store';
import { saveALCAttachmentToIndexedDB } from '../services/ALCOfflineService';
import { getAccessToken } from '../../../../Services/getAccessToken';

const ALCProductionAction: React.FC = () => {
    const { state, dispatch, isProductionUser, isProductUser } = useALCContext();
    const navigate = useNavigate();
    const { instance, accounts } = useMsal();

    const [failedCheckpoints, setFailedCheckpoints] = useState<any[]>(state.failedCheckpoints || []);
    const [actionRemarks, setActionRemarks] = useState<Record<string, string>>({});
    const [actionFiles, setActionFiles] = useState<Record<string, File[]>>({});
    const [existingProdProof, setExistingProdProof] = useState<Record<string, string[]>>({});
    const [isLoadingData, setIsLoadingData] = useState<boolean>(false);

    const qaList: any[] = useSelector((s: any) => s.ALC.config) || [];
    const currentUser = accounts[0]?.name || '';
    const currentEmail = accounts[0]?.username || '';

    // Load configs from SharePoint if missing
    useEffect(() => {
        const fetchConfig = async () => {
            if (!navigator.onLine || qaList.length > 0) return;
            try {
                const response = await instance.acquireTokenSilent({
                    scopes: [SP_MSAL_SCOPE],
                    account: accounts[0],
                });
                const config = await alcService.getConfig(response.accessToken);
                if (config && config.length > 0) {
                    store.dispatch(setALCConfig(config));
                }
            } catch (error) {
                console.error("Failed to load QA config from SharePoint", error);
            }
        };
        fetchConfig();
    }, [instance, accounts, qaList.length]);

    // Fetch latest checkpoints from Dataverse
    useEffect(() => {
        const loadCheckpoints = async () => {
            if (!state.currentTourId) return;
            setIsLoadingData(true);
            try {
                const allCheckpoints = await alcService.getCheckpoints(state.currentTourId);
                const failed = allCheckpoints.filter((c: any) =>
                    c.cr3ea_status === "Not Okay" ||
                    (c.cr3ea_defectcategory && (
                        c.cr3ea_defectcategory.includes("00") ||
                        c.cr3ea_defectcategory.includes("01") ||
                        c.cr3ea_defectcategory.includes("Non-Compliant") ||
                        c.cr3ea_defectcategory.includes("Partial")
                    ))
                );

                setFailedCheckpoints(failed);
                dispatch({ type: 'SET_FAILED_CHECKPOINTS', payload: failed });

                // Initialize prefilled remarks & existing files
                const initRemarks: Record<string, string> = {};
                const initExistingFiles: Record<string, string[]> = {};

                failed.forEach((cp: any) => {
                    const rowKey = cp.cr3ea_rajpura_alcsid || cp.id;
                    const prodRemark = cp.cr3ea_productionremarks || "";

                    if (prodRemark) {
                        let textPart = prodRemark.replace(/^Action:\s*/i, "");
                        let proofFiles: string[] = [];

                        if (textPart.includes("| File:")) {
                            const parts = textPart.split("| File:");
                            textPart = parts[0].trim();
                            proofFiles = parts[1].split(",").map((f: string) => f.trim()).filter(Boolean);
                        }

                        initRemarks[rowKey] = textPart;
                        if (proofFiles.length > 0) {
                            initExistingFiles[rowKey] = proofFiles;
                        }
                    }
                });

                setActionRemarks(initRemarks);
                setExistingProdProof(initExistingFiles);

            } catch (e) {
                console.warn("Failed to load failed checkpoints for production action:", e);
            } finally {
                setIsLoadingData(false);
            }
        };

        if (navigator.onLine) {
            loadCheckpoints();
        }
    }, [state.currentTourId, dispatch]);

    // Derive user assigned areas based on Area Inspector / Product User configs
    const userAssignedAreas = useMemo(() => {
        if (state.userAreas && state.userAreas.length > 0) {
            return state.userAreas;
        }

        if (qaList.length === 0) return [];
        return qaList
            .filter((item: any) => {
                const ct = (item.ConfigType || "").toLowerCase().trim();
                const isAreaType = ct === 'product user' || ct === 'area inspector' || ct === 'product incharge';
                if (!isAreaType || !item.Area) return false;
                return item.AssignedUser?.results?.some((u: any) => {
                    const titleMatch = u.Title?.toLowerCase() === currentUser.toLowerCase() || u.Title?.toLowerCase().includes(currentUser.toLowerCase().split(' ')[0]);
                    const emailMatch = u.EMail && u.EMail.toLowerCase() === currentEmail.toLowerCase();
                    return titleMatch || emailMatch;
                });
            })
            .map((item: any) => item.Area as string)
            .filter(Boolean);
    }, [qaList, currentUser, currentEmail, state.userAreas]);

    // Check if user has area access for a given checkpoint matching corrective-action.js
    const hasAreaAccessForCheckpoint = (cp: any) => {
        if (state.isReadOnly) return true;
        if (isProductionUser) return true; // Shift Executive Production has plant-wide access

        if (userAssignedAreas.length === 0) return true; // fallback if no specific areas configured

        return userAssignedAreas.some((area: string) =>
            cp.cr3ea_area &&
            (cp.cr3ea_area.toLowerCase().includes(area.toLowerCase().trim()) ||
                area.toLowerCase().trim().includes(cp.cr3ea_area.toLowerCase()))
        );
    };

    const handleRemarksChange = (rowKey: string, value: string) => {
        setActionRemarks(prev => ({ ...prev, [rowKey]: value }));
    };

    const handleFileChange = (rowKey: string, files: FileList | null) => {
        if (!files || files.length === 0) return;

        const selectedFiles = Array.from(files);
        const validImages = selectedFiles.filter(file =>
            file.type.startsWith("image/") || /\.(jpe?g|png|gif|webp|bmp|heic)$/i.test(file.name)
        );

        if (validImages.length < selectedFiles.length) {
            alert(`${selectedFiles.length - validImages.length} non-image file(s) were ignored. Only image files (JPG, PNG, WebP) are allowed.`);
        }

        setActionFiles(prev => {
            const current = prev[rowKey] || [];
            const filtered = validImages.filter(f => !current.some(cf => cf.name === f.name && cf.size === f.size));
            return {
                ...prev,
                [rowKey]: [...current, ...filtered]
            };
        });
    };

    const handleRemoveFile = (rowKey: string, fileIdx: number) => {
        setActionFiles(prev => {
            const current = prev[rowKey] || [];
            const updated = current.filter((_, idx) => idx !== fileIdx);
            if (updated.length === 0) {
                const copy = { ...prev };
                delete copy[rowKey];
                return copy;
            }
            return { ...prev, [rowKey]: updated };
        });
    };

    const handleSubmit = async () => {
        if (!state.currentTourId) {
            alert("No active session ID found.");
            return;
        }

        // Validate each checkpoint the user has access to
        let missingRemarksCount = 0;
        let missingFilesCount = 0;

        for (let i = 0; i < failedCheckpoints.length; i++) {
            const cp = failedCheckpoints[i];
            const hasAccess = hasAreaAccessForCheckpoint(cp);
            if (!hasAccess) continue;

            const rowKey = cp.cr3ea_rajpura_alcsid || cp.id;
            const actionTaken = (actionRemarks[rowKey] || "").trim();
            const files = actionFiles[rowKey] || [];
            const existingFiles = existingProdProof[rowKey] || [];
            const hasProof = files.length > 0 || existingFiles.length > 0;

            if (!actionTaken) {
                missingRemarksCount++;
            }

            // Mandatory image check for Non-Compliant checkpoints
            const isNonCompliant = cp.cr3ea_defectcategory &&
                (cp.cr3ea_defectcategory.toLowerCase().includes("non-compliant") ||
                    cp.cr3ea_defectcategory.includes("00") ||
                    cp.cr3ea_defectcategory.includes("01"));

            if (isNonCompliant && !hasProof) {
                missingFilesCount++;
            }
        }

        if (missingRemarksCount > 0 || missingFilesCount > 0) {
            let msg = "";
            if (missingRemarksCount > 0) {
                msg += `Please provide action taken remarks for ${missingRemarksCount} checkpoint(s).\n`;
            }
            if (missingFilesCount > 0) {
                msg += `Uploading an image proof is mandatory for ${missingFilesCount} Non-Compliant checkpoint(s).`;
            }
            alert(msg.trim());
            return;
        }

        const isExpired = state.isPreviousDay ||
            (state.sessionData && (
                (state.sessionData as any).cr3ea_status === "Closed - Expired" ||
                (state.sessionData as any).cr3ea_processstatus === "Closed - Expired"
            ));

        const isSuccessTour = state.sessionData && (
            (state.sessionData as any).cr3ea_processstatus === "Success - Pending Production" ||
            (state.sessionData as any).cr3ea_status === "Success - Pending Production"
        );

        const targetStatus = isExpired
            ? "Closed - Expired"
            : (isSuccessTour ? "Success - Pending Re-Verification" : "Pending Re-Verification");
        const dbStatusValue = targetStatus === "Success - Pending Re-Verification" ? "Pending Re-Verification" : targetStatus;

        const saveOffline = async () => {
            const syncPayloads: any[] = [];

            for (const cp of failedCheckpoints) {
                const hasAccess = hasAreaAccessForCheckpoint(cp);
                if (!hasAccess) continue;

                const rowKey = cp.cr3ea_rajpura_alcsid || cp.id;
                const actionTaken = (actionRemarks[rowKey] || "").trim();
                const files = actionFiles[rowKey] || [];
                const existingFiles = existingProdProof[rowKey] || [];

                if (!actionTaken) continue;

                const allFileNames = [
                    ...existingFiles,
                    ...files.map(f => f.name)
                ];
                const fileString = Array.from(new Set(allFileNames)).join(", ");

                let remarksVal = `Action: ${actionTaken}`;
                if (fileString) {
                    remarksVal += ` | File: ${fileString}`;
                }
                if (remarksVal.length > 1000) {
                    remarksVal = remarksVal.substring(0, 997) + "...";
                }

                for (const f of files) {
                    await saveALCAttachmentToIndexedDB(
                        state.currentTourId!,
                        cp.cr3ea_rajpura_alcsid || cp.id,
                        cp.cr3ea_area,
                        actionTaken,
                        f
                    );
                }

                syncPayloads.push({
                    id: `${Date.now()}_checkrow_${rowKey}`,
                    type: 'CHECKLIST_ROW',
                    method: 'PATCH',
                    payload: {
                        cr3ea_rajpura_alcsid: cp.cr3ea_rajpura_alcsid || cp.id,
                        cr3ea_productionremarks: remarksVal
                    },
                    createdAt: Date.now()
                });
            }

            syncPayloads.push({
                id: `${Date.now()}_session_${state.currentTourId}`,
                type: 'SESSION_UPDATE',
                method: 'PATCH',
                payload: {
                    cr3ea_prod_qualitytourid: state.currentTourId,
                    cr3ea_status: dbStatusValue,
                    cr3ea_processstatus: targetStatus
                },
                createdAt: Date.now()
            });

            store.dispatch(addPendingSync(syncPayloads));

            if (isExpired) {
                alert("Corrective actions saved offline! Since this session is from a previous day, it remains Closed as Expired.");
            } else {
                alert("Corrective actions submitted offline! Assigning back to QA for re-verification upon sync.");
            }

            navigate('/home');
        };

        try {
            dispatch({ type: 'SET_LOADING', payload: true });

            if (!navigator.onLine) {
                await saveOffline();
                return;
            }

            const tokenResult = await getAccessToken(undefined, undefined, true);
            const accessToken = tokenResult?.token;

            // 1. Upload proof files in parallel
            for (const cp of failedCheckpoints) {
                const hasAccess = hasAreaAccessForCheckpoint(cp);
                if (!hasAccess) continue;

                const rowKey = cp.cr3ea_rajpura_alcsid || cp.id;
                const actionTaken = (actionRemarks[rowKey] || "").trim();
                const files = actionFiles[rowKey] || [];
                const existingFiles = existingProdProof[rowKey] || [];

                let uploadedFileNames: string[] = [];

                if (files.length > 0) {
                    const uploadPromises = files.map(file =>
                        alcService.uploadCorrectiveActionFile(
                            file,
                            state.currentTourId!,
                            cp.cr3ea_area,
                            cp.cr3ea_rajpura_alcsid || rowKey,
                            actionTaken,
                            accessToken,
                            instance,
                            accounts[0]
                        ).then(uploadedUrl => {
                            if (uploadedUrl) {
                                return uploadedUrl.substring(uploadedUrl.lastIndexOf("/") + 1);
                            }
                            return file.name;
                        })
                    );
                    uploadedFileNames = await Promise.all(uploadPromises);
                }

                const allFileNames = Array.from(new Set([...existingFiles, ...uploadedFileNames.filter(Boolean)]));
                const fileString = allFileNames.join(", ");

                let remarksVal = `Action: ${actionTaken}`;
                if (fileString) {
                    remarksVal += ` | File: ${fileString}`;
                }
                if (remarksVal.length > 1000) {
                    remarksVal = remarksVal.substring(0, 997) + "...";
                }

                // Update checkpoint record in Dataverse
                await alcService.saveChecklistRow({
                    cr3ea_rajpura_alcsid: cp.cr3ea_rajpura_alcsid,
                    cr3ea_productionremarks: remarksVal
                });
            }

            // 2. Update Tour Session status in Dataverse
            await alcService.saveSession({
                cr3ea_prod_qualitytourid: state.currentTourId,
                cr3ea_status: dbStatusValue,
                cr3ea_processstatus: targetStatus
            });

            if (isExpired) {
                alert("Corrective actions submitted successfully! Since this session is from a previous day, it remains Closed as Expired.");
            } else {
                alert("All corrective actions submitted successfully. Assigning back to QA for re-verification.");
            }

            navigate('/home');

        } catch (error: any) {
            console.error("Error submitting corrective actions:", error);
            if (error.message && (error.message.includes('Failed to fetch') || error.message.includes('Network Error'))) {
                console.warn("Network error during submit, falling back to offline save:", error);
                await saveOffline();
            } else {
                alert("Dataverse Error: Failed to submit corrective actions - " + error.message);
            }
        } finally {
            dispatch({ type: 'SET_LOADING', payload: false });
        }
    };

    return (
        <div id="section-result-fail">
            <ALCStepper />

            {/* Previous Day Warning Banner */}
            {state.isPreviousDay && (
                <div
                    style={{
                        backgroundColor: '#fffbeb',
                        border: '1px solid #fef3c7',
                        borderLeft: '4px solid #f59e0b',
                        padding: '12px 16px',
                        marginBottom: '15px',
                        borderRadius: '6px',
                        fontSize: '13px',
                        color: '#92400e',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px'
                    }}
                >
                    <i className="fa fa-exclamation-triangle" style={{ fontSize: '16px' }}></i>
                    <span>
                        <strong>Previous Day Tour:</strong> This observation was recorded on a previous day. Corrective actions submitted will remain archived under <strong>Closed - Expired</strong> without granting line clearance.
                    </span>
                </div>
            )}

            <div className="bs-card" style={{ marginBottom: '20px', border: '1px solid #cbd5e1', borderRadius: '8px', overflow: 'hidden' }}>
                <div className="bs-card-header" style={{ backgroundColor: '#fee2e2', padding: '14px 20px', borderBottom: '1px solid #fca5a5' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '10px' }}>
                        <div>
                            <h4 className="bs-card-title text-danger" style={{ margin: 0, fontWeight: 'bold', fontSize: '16px' }}>
                                <i className="fa fa-wrench"></i> Production Corrective Action Plan
                            </h4>
                            <p style={{ margin: '4px 0 0 0', fontSize: '13px', color: '#991b1b' }}>
                                Checkpoints marked as Partial or Non-Compliant by QA. Please provide corrective actions and attach proof photos.
                            </p>
                        </div>
                        {isProductUser && userAssignedAreas.length > 0 && (
                            <span style={{ backgroundColor: '#eff6ff', color: '#1d4ed8', border: '1px solid #bfdbfe', padding: '4px 10px', borderRadius: '4px', fontSize: '12px', fontWeight: 600 }}>
                                Assigned Area(s): {userAssignedAreas.join(", ")}
                            </span>
                        )}
                    </div>
                </div>

                <div className="bs-card-body" style={{ padding: '0', overflowX: 'auto' }}>
                    {isLoadingData ? (
                        <div style={{ padding: '30px', textAlign: 'center', color: '#64748b' }}>
                            <div className="spinner-border spinner-border-sm text-primary" role="status"></div> Loading failed checkpoints...
                        </div>
                    ) : failedCheckpoints.length === 0 ? (
                        <div style={{ padding: '30px', textAlign: 'center', color: '#16a34a', fontWeight: 'bold' }}>
                            <i className="fa fa-check-circle" style={{ fontSize: '20px', marginRight: '6px' }}></i>
                            No failed checkpoints found. Line is clear!
                        </div>
                    ) : (
                        <table className="bs-table" style={{ width: '100%', minWidth: '800px', borderCollapse: 'collapse', textAlign: 'center', fontSize: '13px' }}>
                            <thead>
                                <tr style={{ backgroundColor: '#f8fafc', borderBottom: '1px solid #cbd5e1', color: '#475569' }}>
                                    <th style={{ width: '6%', padding: '10px' }}>Sr No.</th>
                                    <th style={{ width: '38%', padding: '10px', textAlign: 'left' }}>Checkpoint Details</th>
                                    <th style={{ width: '16%', padding: '10px' }}>Compliance Score</th>
                                    <th style={{ width: '22%', padding: '10px' }}>Action Taken</th>
                                    <th style={{ width: '18%', padding: '10px' }}>Proof Image</th>
                                </tr>
                            </thead>
                            <tbody>
                                {failedCheckpoints.map((cp, idx) => {
                                    const rowKey = cp.cr3ea_rajpura_alcsid || cp.id;
                                    const hasAccess = hasAreaAccessForCheckpoint(cp);
                                    const actionText = actionRemarks[rowKey] || "";
                                    const files = actionFiles[rowKey] || [];
                                    const existingFiles = existingProdProof[rowKey] || [];

                                    // Parse QA defect remarks and defect photo
                                    const defectRemarks = cp.cr3ea_defectremarks || "";
                                    let cleanQaRemark = defectRemarks;
                                    let qaDefectFiles: string[] = [];

                                    if (defectRemarks.toLowerCase().includes("file:")) {
                                        const fIdx = defectRemarks.toLowerCase().indexOf("file:");
                                        const filePart = defectRemarks.substring(fIdx + 5).trim();
                                        let textPart = defectRemarks.substring(0, fIdx).trim();
                                        if (textPart.endsWith("|")) {
                                            textPart = textPart.substring(0, textPart.length - 1).trim();
                                        }
                                        cleanQaRemark = textPart;
                                        qaDefectFiles = filePart.split(",").map((f: string) => f.trim()).filter(Boolean);
                                    }

                                    return (
                                        <tr
                                            key={idx}
                                            style={{
                                                borderBottom: '1px solid #e2e8f0',
                                                backgroundColor: hasAccess ? '#ffffff' : '#f8fafc',
                                                opacity: hasAccess ? 1 : 0.65
                                            }}
                                        >
                                            <td style={{ padding: '10px' }}>{idx + 1}</td>
                                            <td style={{ padding: '10px', textAlign: 'left' }}>
                                                <strong style={{ color: '#0f172a' }}>{cp.cr3ea_area}</strong>
                                                <div style={{ color: '#475569', fontSize: '13px', marginTop: '2px' }}>{cp.cr3ea_criteria}</div>

                                                {/* QA Defect Notice Box */}
                                                <div style={{ marginTop: '6px', fontSize: '12px', color: '#991b1b', backgroundColor: '#fef2f2', padding: '6px 10px', borderRadius: '4px', border: '1px solid #fee2e2' }}>
                                                    <strong><i className="fa fa-info-circle"></i> QA Defect:</strong> {cleanQaRemark || "Non-Compliant"}
                                                    {qaDefectFiles.length > 0 && (
                                                        <div style={{ display: 'inline-flex', gap: '4px', marginLeft: '6px' }}>
                                                            {qaDefectFiles.map((_qf, qfIdx) => (
                                                                <span
                                                                    key={qfIdx}
                                                                    style={{
                                                                        backgroundColor: '#fee2e2',
                                                                        color: '#b91c1c',
                                                                        padding: '1px 5px',
                                                                        borderRadius: '3px',
                                                                        fontSize: '10px',
                                                                        fontWeight: 600,
                                                                        border: '1px solid #fca5a5'
                                                                    }}
                                                                >
                                                                    Defect Photo {qaDefectFiles.length > 1 ? qfIdx + 1 : ''}
                                                                </span>
                                                            ))}
                                                        </div>
                                                    )}
                                                </div>
                                            </td>

                                            <td style={{ padding: '10px' }}>
                                                <span
                                                    style={{
                                                        backgroundColor: '#fee2e2',
                                                        color: '#b91c1c',
                                                        border: '1px solid #fca5a5',
                                                        padding: '4px 8px',
                                                        borderRadius: '4px',
                                                        fontSize: '12px',
                                                        fontWeight: 600
                                                    }}
                                                >
                                                    {cp.cr3ea_defectcategory || "Non-Compliant (0)"}
                                                </span>
                                            </td>

                                            <td style={{ padding: '10px' }}>
                                                <input
                                                    type="text"
                                                    className="form-control"
                                                    placeholder={hasAccess ? "Enter corrective action taken" : "Locked for other area owner"}
                                                    value={actionText}
                                                    disabled={!hasAccess || state.isReadOnly}
                                                    onChange={(e) => handleRemarksChange(rowKey, e.target.value)}
                                                    style={{
                                                        width: '100%',
                                                        padding: '6px 10px',
                                                        borderRadius: '4px',
                                                        fontSize: '13px',
                                                        borderColor: '#cbd5e1'
                                                    }}
                                                />
                                            </td>

                                            <td style={{ padding: '10px' }}>
                                                <div
                                                    className="custom-file-upload"
                                                    style={{
                                                        border: '1px dashed #cbd5e1',
                                                        padding: '6px',
                                                        borderRadius: '4px',
                                                        backgroundColor: hasAccess ? '#ffffff' : '#f1f5f9'
                                                    }}
                                                >
                                                    <input
                                                        type="file"
                                                        accept="image/*"
                                                        multiple
                                                        disabled={!hasAccess || state.isReadOnly}
                                                        onChange={(e) => handleFileChange(rowKey, e.target.files)}
                                                        style={{ fontSize: '11px', width: '100%' }}
                                                    />

                                                    {/* Existing saved proof links */}
                                                    {existingFiles.length > 0 && (
                                                        <div style={{ marginTop: '4px', textAlign: 'left', fontSize: '11px', color: '#166534' }}>
                                                            <strong><i className="fa fa-check-circle"></i> Saved Proof:</strong>
                                                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px', marginTop: '2px' }}>
                                                                {existingFiles.map((_ef, efIdx) => (
                                                                    <span
                                                                        key={efIdx}
                                                                        style={{
                                                                            backgroundColor: '#f0fdf4',
                                                                            border: '1px solid #bbf7d0',
                                                                            padding: '1px 5px',
                                                                            borderRadius: '3px',
                                                                            fontSize: '10px'
                                                                        }}
                                                                    >
                                                                        Proof {efIdx + 1}
                                                                    </span>
                                                                ))}
                                                            </div>
                                                        </div>
                                                    )}

                                                    {/* Newly uploaded file chips */}
                                                    {files.length > 0 && (
                                                        <div style={{ marginTop: '4px', textAlign: 'left' }}>
                                                            <div style={{ fontSize: '11px', fontWeight: 600, color: '#15803d' }}>
                                                                {files.length} photo(s) selected:
                                                            </div>
                                                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px', marginTop: '2px' }}>
                                                                {files.map((file, fIdx) => (
                                                                    <div
                                                                        key={fIdx}
                                                                        style={{
                                                                            display: 'inline-flex',
                                                                            alignItems: 'center',
                                                                            gap: '4px',
                                                                            backgroundColor: '#f0fdf4',
                                                                            border: '1px solid #bbf7d0',
                                                                            padding: '2px 5px',
                                                                            borderRadius: '3px',
                                                                            fontSize: '11px',
                                                                            color: '#166534'
                                                                        }}
                                                                    >
                                                                        <span style={{ maxWidth: '90px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={file.name}>
                                                                            {file.name}
                                                                        </span>
                                                                        <button
                                                                            type="button"
                                                                            onClick={() => handleRemoveFile(rowKey, fIdx)}
                                                                            style={{
                                                                                background: 'none',
                                                                                border: 'none',
                                                                                color: '#991b1b',
                                                                                cursor: 'pointer',
                                                                                fontWeight: 'bold'
                                                                            }}
                                                                        >
                                                                            &times;
                                                                        </button>
                                                                    </div>
                                                                ))}
                                                            </div>
                                                        </div>
                                                    )}

                                                    {files.length === 0 && existingFiles.length === 0 && (
                                                        <div style={{ fontSize: '11px', color: '#94a3b8', marginTop: '3px' }}>
                                                            No image uploaded
                                                        </div>
                                                    )}
                                                </div>
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    )}
                </div>
            </div>

            {/* Submit Action Button */}
            <div
                className="tour-cyle-btn-wrapper"
                style={{
                    display: 'flex',
                    justifyContent: 'flex-end',
                    alignItems: 'center',
                    gap: '12px',
                    margin: '25px 0'
                }}
            >
                <button
                    type="button"
                    id="btn-submit-corrective-actions"
                    onClick={handleSubmit}
                    disabled={state.isLoading || state.isReadOnly || failedCheckpoints.length === 0}
                    style={{
                        padding: '10px 28px',
                        backgroundColor: '#2563eb',
                        color: '#ffffff',
                        border: 'none',
                        borderRadius: '6px',
                        fontWeight: 600,
                        fontSize: '14px',
                        cursor: 'pointer',
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '6px'
                    }}
                >
                    {state.isLoading ? (
                        <>
                            <span className="spinner-border spinner-border-sm" role="status"></span> Submitting Actions...
                        </>
                    ) : (
                        <>
                            <i className="fa fa-paper-plane"></i> Submit Actions &amp; Assign to QA
                        </>
                    )}
                </button>
            </div>
        </div>
    );
};

export default ALCProductionAction;
